<script setup lang="ts">
import { ref, toRef } from 'vue';
import { IonLabel } from '@ionic/vue';
import { useField, useIsFormValid } from 'vee-validate';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const props = defineProps({
	disabled: { type: Boolean, default: false },
	iconLeft: { type: String, default: '' },
	inputClass: { type: String, default: 'font-sourcesanspro-regular text-base' },
	inputMode: {
		type: String as () => 'text' | 'search' | 'email' | 'tel' | 'url' | 'none' | 'numeric' | 'decimal' | undefined,
		default: 'text',
	},
	label: { type: String },
	name: { type: String, required: true },
	manualSubmit: { type: Function },
	placeholder: { type: String, default: '' },
	step: { type: String as () => string | undefined },
	showErrorMessage: { type: Boolean, default: true },
	showInputSuccess: { type: Boolean, default: true },
	shouldHandleBlur: { type: Boolean, default: true },
	shouldValidate: { type: Boolean, default: true },
	successMessage: { type: String, default: '' },
	type: { type: String, default: 'text' },
	value: { type: String as () => string | number | null | undefined, default: null },
	wrapperClass: { type: String, default: '' },
});

const emit = defineEmits(['inputChanged', 'enterHit']);

// use `toRef` to create reactive references to `name` prop which is passed to `useField`
// this is important because vee-validte needs to know if the field name changes
// https://vee-validate.logaretm.com/v4/guide/composition-api/caveats
const name = toRef(props, 'name');

// we don't provide any rules here because we are using form-level validation
// https://vee-validate.logaretm.com/v4/guide/validation#form-level-validation
const { value, errorMessage, handleChange, meta, setValue, setErrors, validate } = useField(name, undefined, {
	initialValue: props.value,
});

const isFormValid = useIsFormValid();
const formattedDate = ref(value);

const updateDate = (event: any) => {
	if (event.target.value) {
		formattedDate.value = event.target.value;
		// eslint-disable-next-line vue/no-ref-as-operand
		emit('inputChanged', isFormValid, name);
	}
};
</script>
<template>
	<div v-show="type !== 'hidden'" class="flex flex-col w-full">
		<div v-if="props.label" class="mb-2">
			<ion-label class="font-sourcesanspro-regular">{{ props.label }}</ion-label>
		</div>
		<div
			class="wrapper border-white-15"
			:class="[
				{
					'border-error': !!errorMessage && !!wrapperClass,
					'border-success': props.showInputSuccess && meta.valid && meta.dirty,
				},
				wrapperClass,
			]">
			<div class="flex w-full items-center px-6">
				<input v-mask="'##-##-####'" class="font-sourcesanspro-regular" type="text" id="dateInput" :name="props.name" :value="formattedDate" :placeholder="$t('components.date_placeholder')" @input="(event) => updateDate(event)" />
			</div>
		</div>
		<p class="mb-4 font-sourcesanspro-regular text-sm font-color-error" v-show="errorMessage && showErrorMessage">
			{{ errorMessage }}
		</p>
	</div>
</template>

<style lang="scss" scoped>
ion-label {
	color: rgb(255 255 255 / 0.5) !important;
	font-size: 12px !important;
}

input {
	border: none;
	outline: none;
	background: none;
	height: 100%;
	width: 100%;
}

div.wrapper {
	border-radius: 9999px;
	height: 52px;

	p {
		&::first-letter {
			text-transform: capitalize;
		}
	}

	ion-icon {
		cursor: pointer;
	}

	// .border-success input {
	//   background-color: var(--success-bg-color);
	//   color: var(--success-color);
	// }

	&.border-success {
		border: 1px solid var(--clientx-color-success) !important;

		ion-icon {
			color: var(--clientx-color-success);
		}
	}
}
</style>
